import { keys } from '@/app/i18n'
import type { NavItem } from '@/app/nav/collect'
import { EMPLOYER_SOFTWARES } from '@/app/stores/session'

/**
 * The vertical menu of every software (legacy `navigation/vertical/{index,common,recruiting,onboarding,hr,engagement,
 * accounting,candidate,employee}.js`, wp/shell §3 table), owned by the shell. Targets are the manifest's new paths
 * (D-15); until a module lands they open its "Not migrated yet" placeholder. A module adds items of its own in its
 * `nav.ts`; it does not redeclare these (ids are unique across modules).
 *
 * Filtering (src/app/nav/collect.ts `filterNav`): software, user type, ACL resource (D-21 — children too), beta flag,
 * workspace. Legacy filtered top-level items only and hid an item without `module` from Staff (NEW-10: the Engagement
 * "Employees" group now uses `employees`, OPEN-15 default (a)).
 */

const STAFF_AND_CLIENTS = ['Client', 'Staff'] as const
const FREELANCERS = ['Freelancer'] as const
/**
 * The legacy employer softwares: Projects (`pm`, docs/projects) has its own tasks and time, and lists no calendar, HR
 * tasks, notes or documents, which scope their records to the five legacy softwares (`Soft1`–`Soft5`).
 */
const LEGACY_EMPLOYER = ['re', 'on', 'hr', 'en', 'ac'] as const

const nav: NavItem[] = [
  // Legacy `index.js:103-111`: prepended when the software is not `ac` and there is a current workspace. Accounting has
  // one too since 2026-10-26 (owner's request): `/ac/dashboard`, the widget board of the finances module.
  {
    id: 'dashboard',
    title: keys.t('common.nav.dashboard'),
    icon: 'MonitorIcon',
    to: ({ software }) => `/${software}/dashboard`,
    softwares: ['re', 'on', 'hr', 'en', 'ac', 'ca', 'em'],
    resource: 'always',
    requiresWorkspace: true,
    order: 0,
  },

  // --- Recruiting --------------------------------------------------------------------------------------------------
  {
    id: 're-cvs',
    title: keys.t('common.nav.cvDatabase'),
    icon: 'ArchiveIcon',
    to: '/re/cvs',
    softwares: ['re'],
    resource: 'cv',
    order: 10,
  },
  {
    id: 're-job-offers',
    title: keys.t('common.nav.jobOffers'),
    icon: 'BriefcaseIcon',
    to: '/re/job-offers',
    softwares: ['re'],
    resource: 'job-offers',
    order: 20,
  },
  {
    id: 're-applications',
    title: keys.t('common.nav.applications'),
    icon: 'UsersIcon',
    // Legacy `{ name: 'applications', params: { stage: 'ALL' } }`; the stage is a query parameter now (manifest).
    to: { path: '/re/applications', query: { stage: 'ALL' } },
    softwares: ['re'],
    resource: 'applications',
    order: 30,
  },

  // --- Human resources (Onboarding and Engagement are part of it since 2026-11-03, owner's call, BR-097) -----------
  {
    id: 'employees',
    title: keys.t('common.nav.employees'),
    icon: 'UsersIcon',
    softwares: ['hr'],
    resource: 'employees',
    order: 10,
    children: [
      {
        id: 'employees-list',
        title: keys.t('common.nav.employees'),
        to: ({ software }) => `/${software}/employees`,
        softwares: ['hr'],
        order: 1,
      },
      {
        id: 'employees-teams',
        title: keys.t('common.nav.teams'),
        to: ({ software }) => `/${software}/employee-groups`,
        softwares: ['hr'],
        order: 2,
      },
      {
        id: 'employees-org-chart',
        title: keys.t('common.nav.organisationChart'),
        to: ({ software }) => `/${software}/org-chart`,
        softwares: ['hr'],
        beta: true,
        order: 3,
      },
    ],
  },
  {
    id: 'hr-onboarding',
    title: keys.t('common.nav.onboarding'),
    icon: 'LogInIcon',
    // Legacy `/on/onboarding/0`: the id was the stage filter (manifest: `?stage=`).
    to: { path: '/hr/onboarding', query: { stage: '0' } },
    softwares: ['hr'],
    resource: 'onboarding',
    order: 20,
  },
  {
    id: 'hr-offboarding',
    title: keys.t('common.nav.offboarding'),
    icon: 'LogOutIcon',
    to: { path: '/hr/offboarding', query: { stage: '0' } },
    softwares: ['hr'],
    resource: 'offboarding',
    order: 30,
  },
  {
    id: 'hr-time-off',
    title: keys.t('common.nav.timeOff'),
    icon: 'BellOffIcon',
    to: '/hr/leave-absence',
    softwares: ['hr'],
    resource: 'leave_absence',
    badge: 'leavesCount',
    order: 40,
  },
  {
    id: 'hr-timesheets',
    title: keys.t('common.nav.timesheets'),
    icon: 'ClockIcon',
    to: '/hr/timesheets',
    softwares: ['hr'],
    resource: 'timesheets',
    badge: 'timesheetsCount',
    order: 50,
  },
  {
    id: 'hr-surveys',
    title: keys.t('common.nav.surveys'),
    icon: 'ClipboardIcon',
    softwares: ['hr'],
    resource: 'surveys',
    order: 60,
    children: [
      { id: 'hr-surveys-list', title: keys.t('common.nav.surveys'), to: '/hr/surveys', softwares: ['hr'], order: 1 },
      {
        id: 'hr-survey-templates',
        title: keys.t('common.nav.surveyTemplates'),
        to: '/hr/surveys/templates',
        softwares: ['hr'],
        order: 2,
      },
      {
        id: 'hr-survey-questions',
        title: keys.t('common.nav.questionTemplates'),
        to: '/hr/survey-questions',
        softwares: ['hr'],
        order: 3,
      },
    ],
  },
  {
    id: 'hr-suggestions',
    title: keys.t('common.nav.suggestions'),
    icon: 'InboxIcon',
    to: '/hr/suggestions',
    softwares: ['hr'],
    resource: 'suggestions',
    order: 70,
  },

  // --- Accounting ----------------------------------------------------------------------------------------------------
  {
    id: 'ac-finances',
    title: keys.t('common.nav.finances'),
    icon: 'BanknoteIcon',
    softwares: ['ac'],
    resource: 'finances',
    badge: 'finGlobal',
    order: 10,
    children: [
      {
        id: 'ac-finances-overview',
        title: keys.t('common.nav.overview'),
        icon: 'PieChartIcon',
        to: '/ac/finances',
        softwares: ['ac'],
        resource: 'finances',
        order: 1,
      },
      {
        id: 'ac-invoices',
        title: keys.t('common.nav.salesInvoices'),
        icon: 'FilePlusIcon',
        to: '/ac/invoices',
        softwares: ['ac'],
        resource: 'invoices',
        badge: 'notPaidIncomes',
        order: 2,
      },
      {
        id: 'ac-expenses',
        title: keys.t('common.nav.purchaseInvoices'),
        icon: 'FileMinusIcon',
        to: '/ac/expenses',
        softwares: ['ac'],
        resource: 'invoices',
        badge: 'notPaidExpenses',
        order: 3,
      },
    ],
  },
  {
    id: 'ac-quotes',
    title: keys.t('common.nav.quotes'),
    icon: 'BriefcaseIcon',
    to: '/ac/quotes',
    softwares: ['ac'],
    resource: 'invoices',
    order: 20,
  },
  {
    id: 'ac-vendors',
    title: keys.t('common.nav.clientsVendors'),
    icon: 'UsersIcon',
    to: '/ac/vendors',
    softwares: ['ac'],
    resource: 'vendors',
    order: 30,
  },
  {
    id: 'ac-bank',
    title: keys.t('common.nav.bank'),
    icon: 'BookOpenIcon',
    softwares: ['ac'],
    resource: 'bank',
    order: 40,
    children: [
      {
        id: 'ac-bank-accounts',
        title: keys.t('common.nav.accounts'),
        icon: 'BookOpenIcon',
        to: '/ac/bank-accounts',
        softwares: ['ac'],
        order: 1,
      },
      {
        id: 'ac-transactions',
        title: keys.t('common.nav.transactions'),
        icon: 'CreditCardIcon',
        to: '/ac/transactions',
        softwares: ['ac'],
        order: 2,
      },
    ],
  },

  // --- Common section (every employer software; Calendar joined Accounting on 2026-10-30) ----------------------------
  {
    id: 'calendar',
    section: 'workspace',
    title: keys.t('common.nav.calendar'),
    icon: 'CalendarIcon',
    to: ({ software }) => `/${software}/calendar`,
    softwares: LEGACY_EMPLOYER,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'calendar',
    order: 100,
  },
  {
    id: 'tasks',
    section: 'workspace',
    title: keys.t('common.nav.tasks'),
    icon: 'CheckCircleIcon',
    softwares: LEGACY_EMPLOYER,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'tasks',
    order: 110,
    // One link since task templates and groups were dropped (2026-10-29, owner's call, as in Documents).
    to: ({ software }) => `/${software}/tasks`,
  },
  {
    // Always shown, right after Tasks (owner, 2026-10-31): Notes has no permission of its own — each note's
    // "Who can see" decides, on the API.
    id: 'notes',
    section: 'workspace',
    title: keys.t('common.nav.notes'),
    icon: 'StickyNoteIcon',
    softwares: LEGACY_EMPLOYER,
    userTypes: STAFF_AND_CLIENTS,
    order: 115,
    to: ({ software }) => `/${software}/notes`,
    // Remind me: my reminders on notes due today or earlier
    badge: 'notesDue',
  },
  {
    id: 'documents',
    section: 'workspace',
    title: keys.t('common.nav.documents'),
    icon: 'FolderIcon',
    softwares: LEGACY_EMPLOYER,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'documents',
    order: 120,
    // One link since document groups were dropped (2026-10-28, owner's call; templates went on 2026-10-27).
    to: ({ software }) => `/${software}/documents`,
    // BR-095: the documents ending soon or ended — the reminder (also in the navbar's alerts)
    badge: 'documentsExpiring',
  },
  {
    id: 'messages',
    section: 'workspace',
    title: keys.t('shell.navbar.action.messages'),
    icon: 'MessageCircleIcon',
    to: '/messages',
    // The chat (docs/migration/wp/messages): Freelancers too — they write to their employer.
    softwares: ['re', 'on', 'hr', 'en', 'ac', 'pm', 'ca', 'em'],
    resource: 'always',
    // The chat is a workspace's (a candidate without an application has nobody to write to).
    requiresWorkspace: true,
    badge: 'chatUnread',
    order: 125,
  },
  {
    id: 'company-profile',
    section: 'account',
    title: keys.t('common.nav.organizationProfile'),
    icon: 'LayoutIcon',
    to: ({ software }) => `/${software}/company-profile`,
    softwares: EMPLOYER_SOFTWARES,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'company-profile',
    order: 130,
  },
  {
    id: 'settings',
    section: 'account',
    title: keys.t('common.nav.settings'),
    icon: 'SettingsIcon',
    to: ({ software }) => `/${software}/settings/configuration`,
    softwares: EMPLOYER_SOFTWARES,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'always',
    order: 140,
  },
  {
    id: 'trash',
    section: 'workspace',
    title: keys.t('common.nav.trash'),
    icon: 'Trash2Icon',
    to: ({ software }) => `/${software}/trash`,
    softwares: EMPLOYER_SOFTWARES,
    userTypes: STAFF_AND_CLIENTS,
    resource: 'always',
    badge: 'trashCount',
    order: 150,
  },

  // --- Candidate (Freelancer) ----------------------------------------------------------------------------------------
  {
    id: 'ca-resume',
    title: keys.t('common.nav.myResume'),
    icon: 'FileTextIcon',
    to: '/ca/resume',
    softwares: ['ca'],
    userTypes: FREELANCERS,
    resource: 'always',
    order: 10,
  },
  {
    id: 'ca-applications',
    title: keys.t('common.nav.applications'),
    icon: 'FileIcon',
    to: '/ca/applications',
    softwares: ['ca'],
    userTypes: FREELANCERS,
    resource: 'always',
    requiresWorkspace: true,
    order: 20,
  },
  {
    id: 'ca-tasks',
    title: keys.t('common.nav.tasks'),
    icon: 'CheckCircleIcon',
    to: '/ca/tasks',
    softwares: ['ca'],
    userTypes: FREELANCERS,
    resource: 'always',
    requiresWorkspace: true,
    order: 30,
  },
  {
    id: 'ca-documents',
    title: keys.t('common.nav.documents'),
    icon: 'FolderIcon',
    to: '/ca/documents',
    softwares: ['ca'],
    userTypes: FREELANCERS,
    resource: 'always',
    requiresWorkspace: true,
    order: 40,
  },

  // --- Employee (Freelancer); legacy gated these by module, which a Freelancer always passes -----------------------
  {
    id: 'em-time-off',
    title: keys.t('common.nav.timeOff'),
    icon: 'BellOffIcon',
    to: '/em/leave-absence',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'leave_absence',
    badge: 'leavesCount',
    order: 10,
  },
  {
    id: 'em-timesheets',
    title: keys.t('common.nav.timesheets'),
    icon: 'ClockIcon',
    to: '/em/timesheets',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'timesheets',
    badge: 'timesheetsCount',
    order: 20,
  },
  {
    id: 'em-surveys',
    title: keys.t('common.nav.surveys'),
    icon: 'ClipboardIcon',
    to: '/em/surveys',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'surveys',
    order: 30,
  },
  {
    id: 'em-suggestions',
    title: keys.t('common.nav.suggestions'),
    icon: 'InboxIcon',
    to: '/em/suggestions',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'suggestions',
    order: 40,
  },
  {
    id: 'em-tasks',
    title: keys.t('common.nav.tasks'),
    icon: 'CheckCircleIcon',
    to: '/em/tasks',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'tasks',
    order: 50,
  },
  {
    id: 'em-documents',
    title: keys.t('common.nav.documents'),
    icon: 'FolderIcon',
    to: '/em/documents',
    softwares: ['em'],
    userTypes: FREELANCERS,
    resource: 'documents',
    order: 60,
  },
]

export default nav
